import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft, Mail, Phone, MapPin, Building, Lock, AlertTriangle, Pause, Play,
  ShieldOff, ShieldCheck, RefreshCcw, ArchiveX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { BrandLogo } from "@/components/BrandLogo";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { format } from "@/lib/brand-tokens";
import {
  members, getMemberById, getReservationsByMember, getPaymentItemsByMember,
  getDealById, getBrandById, getPaymentRunById, auditEvents, updateMemberStatus,
  markPaymentItemReturned, markPaymentItemRefunded,
} from "@/lib/mock-data";
import { Gated } from "@/components/Gated";
import { toast } from "sonner";

// ADM-13 — Member detail (admin)
export default function AdminMemberDetailPage() {
  const { memberId } = useParams();
  const m = getMemberById(memberId) || members[0];
  const reservations = getReservationsByMember(m.id);
  const payments = getPaymentItemsByMember(m.id);
  const memberAudit = auditEvents.filter((e) => e.actorId === m.id || e.entityId === m.id).slice(0, 8);
  const lifetimeCompleted = payments.filter((p) => p.status === "Completed").reduce((a, p) => a + p.amount, 0);
  const pending = payments.filter((p) => p.status === "Scheduled" || p.status === "Sent").reduce((a, p) => a + p.amount, 0);

  // EDGE CASE — returned payments (R03 etc.)
  const returnedPayments = payments.filter((p) => p.status === "Returned");
  const isDeactivated = m.status === "Deactivated";

  const [suspendOpen, setSuspendOpen] = useState(false);
  const [reason, setReason] = useState("");
  // Manual return/refund (member payments section)
  const [actionDialog, setActionDialog] = useState(null);
  const [actionReason, setActionReason] = useState("");

  const openAction = (kind, itemId) => {
    setActionReason(kind === "returned" ? "R03 — No Account / Unable to Locate Account" : "Member request");
    setActionDialog({ kind, itemId });
  };
  const confirmAction = () => {
    if (!actionDialog) return;
    const r = actionReason.trim() || null;
    if (actionDialog.kind === "returned") markPaymentItemReturned(actionDialog.itemId, r);
    else                                  markPaymentItemRefunded(actionDialog.itemId, r);
    toast.success(`Marked ${actionDialog.kind}`, { description: r });
    setActionDialog(null);
  };

  return (
    <div data-testid="admin-member-detail-page" className="space-y-5 pb-10">
      <Breadcrumbs items={[
        { label: "Admin", to: "/admin/dashboard" },
        { label: "Members", to: "/admin/members" },
        { label: `${m.firstName} ${m.lastName}` },
      ]} />

      {/* Header */}
      <div className="bg-surface border border-line rounded-xl p-6 flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="size-14 rounded-full bg-brand-50 text-brand-700 grid place-items-center text-h2 font-brand font-semibold">
            {m.firstName[0]}{m.lastName[0]}
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-brand text-h1 text-ink font-semibold">{m.firstName} {m.lastName}</h1>
              <StatusBadge entity="member" status={m.status} dot />
            </div>
            <div className="mt-1 text-caption text-ink-subtle font-mono">{m.id}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {m.status === "Active" ? (
            <Gated action="members.suspend">
              <Button variant="outline" onClick={() => setSuspendOpen(true)} data-testid="suspend-member-btn"
                      className="border-amber-200 text-amber-700 hover:bg-amber-50">
                <Pause className="size-4 mr-1.5" /> Suspend
              </Button>
            </Gated>
          ) : m.status === "Suspended" ? (
            <Gated action="members.reactivate">
              <Button onClick={() => { updateMemberStatus(m.id, "Active"); toast.success("Member reactivated"); }}
                      data-testid="reactivate-member-btn"
                      className="bg-brand-700 hover:bg-brand-800 text-white">
                <Play className="size-4 mr-1.5" /> Reactivate
              </Button>
            </Gated>
          ) : null}
          {!isDeactivated && <Button variant="outline"><Mail className="size-4 mr-1.5" /> Send email</Button>}
        </div>
      </div>

      {/* Deactivated-account banner — closed accounts have a 30-day data retention window. */}
      {isDeactivated && (
        <div data-testid="deactivated-banner" className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex items-start gap-3">
          <ArchiveX className="size-5 text-slate-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-2">
              <h3 className="text-h3 text-ink font-semibold">Account closed</h3>
              {m.deactivatedAt && <span className="text-caption text-ink-subtle">on {format.date(m.deactivatedAt)}</span>}
            </div>
            <p className="mt-1.5 text-body-sm text-ink-muted leading-relaxed">
              {m.suspendReason || "Member-initiated account closure."} Personal data and bank credentials are deleted after the 30-day retention window. Transaction records and audit history are retained for tax/compliance purposes.
            </p>
            <div className="mt-3 flex flex-wrap gap-3 text-caption text-ink-subtle">
              <span>Lifetime earnings on record: <strong className="text-ink tabular-nums">{format.currency(m.lifetimeEarnings || 0)}</strong></span>
              <span>·</span>
              <span>Last sign-in: <strong className="text-ink">{m.lastSignIn ? format.date(m.lastSignIn) : "—"}</strong></span>
            </div>
          </div>
        </div>
      )}

      {/* Returned-payment investigation — surfaces ACH return reasons (R03 etc.) requiring resolution. */}
      {returnedPayments.length > 0 && (
        <div data-testid="returned-payments-banner" className="bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <RefreshCcw className="size-5 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-2">
                <h3 className="text-h3 text-ink font-semibold">{returnedPayments.length} returned ACH {returnedPayments.length === 1 ? "payment" : "payments"}</h3>
                <span className="text-caption text-red-700 font-semibold">Needs resolution</span>
              </div>
              <p className="mt-1.5 text-body-sm text-ink-muted">Funds were returned by the receiving bank. Update bank details and re-issue the payment in the next run.</p>
            </div>
          </div>
          <div className="mt-4 -mx-5 -mb-5 border-t border-red-200 bg-white/60 rounded-b-xl">
            <table className="w-full text-body-sm">
              <thead>
                <tr className="text-left text-eyebrow uppercase text-red-900/70 border-b border-red-200">
                  <th className="px-5 py-2.5">Payment</th>
                  <th className="px-3 py-2.5">Run</th>
                  <th className="px-3 py-2.5">Return code</th>
                  <th className="px-3 py-2.5">Returned on</th>
                  <th className="px-5 py-2.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-100">
                {returnedPayments.map((p) => {
                  const run = getPaymentRunById(p.runId);
                  return (
                    <tr key={p.id}>
                      <td className="px-5 py-2.5 font-mono text-caption text-ink-muted">{p.id}</td>
                      <td className="px-3 py-2.5 font-mono text-caption">
                        {run ? <Link to={`/admin/payments/${run.id}`} className="text-brand-700 hover:underline">{run.id}</Link> : <span className="text-ink-hint">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-ink">{p.returnReason || "—"}</td>
                      <td className="px-3 py-2.5 text-ink-muted">{p.returnedAt ? format.date(p.returnedAt) : "—"}</td>
                      <td className="px-5 py-2.5 text-ink tabular-nums text-right">{format.currency(p.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Main */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-surface border border-line rounded-xl p-6">
            <h2 className="text-h3 text-ink font-semibold">Contact &amp; identity</h2>
            <dl className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-4 text-body-sm">
              <Field icon={<Mail className="size-3.5" />} label="Email" value={m.email} />
              <Field icon={<Phone className="size-3.5" />} label="Phone" value={format.phone(m.phone)} />
              <Field icon={<MapPin className="size-3.5" />} label="Location" value={`${m.city}, ${m.state}`} />
              <Field icon={<Building className="size-3.5" />} label="Member since" value={m.memberSince ? format.date(m.memberSince) : "—"} />
            </dl>
          </div>

          {/* Bank (admin view, decryption-protected) */}
          <div className="bg-surface border border-line rounded-xl p-6">
            <h2 className="text-h3 text-ink font-semibold">Bank account</h2>
            {isDeactivated ? (
              <>
                <p className="mt-1 text-caption text-ink-subtle">Bank details were deleted at account closure. Returned-payment audit trail is preserved.</p>
                <div className="mt-4 bg-surface-alt border border-line rounded-md p-4 flex items-start gap-3">
                  <ArchiveX className="size-4 text-slate-500 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-body-sm font-semibold text-ink">Deleted</div>
                    <div className="text-caption text-ink-muted">Bank credentials purged on {m.deactivatedAt ? format.date(m.deactivatedAt) : "account closure"}.</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="mt-1 text-caption text-ink-subtle">Decryption requires Payments Overseer permission and is logged.</p>
                <div className="mt-4 bg-surface-alt border border-line rounded-md p-4 flex items-start gap-3">
                  <Lock className="size-4 text-orange-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-body-sm font-semibold text-ink">{m.bankName || "—"}</div>
                    <div className="text-caption text-ink-muted font-mono">Routing •••••••••<br/>Account {format.bankLast4(m.accountLast4)} · {m.accountType}</div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Reservations */}
          <div className="bg-surface border border-line rounded-xl">
            <div className="px-6 py-4 border-b border-line">
              <h2 className="text-h3 text-ink font-semibold">Reservations <span className="text-ink-hint font-normal tabular-nums">({reservations.length})</span></h2>
            </div>
            {reservations.length === 0 ? (
              <div className="p-8 text-center text-body-sm text-ink-muted">No reservations on record.</div>
            ) : (
              <table className="w-full text-body-sm">
                <thead className="bg-surface-alt">
                  <tr className="text-left">
                    <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint">Deal</th>
                    <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
                    <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Offered</th>
                    <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Submitted</th>
                    <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint text-right">Payout</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/60">
                  {reservations.slice(0, 8).map((r) => {
                    const deal = getDealById(r.dealId);
                    // Each reservation is scoped to one specific brand even on
                    // multi-brand deals — use that, not the deal's primary.
                    const brand = getBrandById(r.brandId || deal?.brandId);
                    return (
                      <tr key={r.id} className="hover:bg-surface-alt/40">
                        <td className="px-6 py-3"><div className="flex items-center gap-2.5"><BrandLogo brand={brand} size="xs" /><div className="min-w-0"><div className="text-ink font-medium truncate max-w-[220px]">{deal?.name}</div><div className="text-caption text-ink-subtle truncate max-w-[220px]">{brand?.name}</div></div></div></td>
                        <td className="px-3 py-3"><StatusBadge entity="reservation" status={r.status} /></td>
                        <td className="px-3 py-3 text-ink tabular-nums text-right">{format.currency(r.faceValueReserved)}</td>
                        <td className="px-3 py-3 text-ink-muted tabular-nums text-right">{format.currency(r.faceValueSubmitted || 0)}</td>
                        <td className="px-6 py-3 text-brand-700 font-semibold tabular-nums text-right">{format.currency(r.expectedPayout)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Payments — admin can manually flip status (Returned / Refunded) per item */}
          {payments.length > 0 && !isDeactivated && (
            <div data-testid="member-payments-table" className="bg-surface border border-line rounded-xl">
              <div className="px-6 py-4 border-b border-line flex items-center justify-between">
                <h2 className="text-h3 text-ink font-semibold">Payments <span className="text-ink-hint font-normal tabular-nums">({payments.length})</span></h2>
                <span className="text-caption text-ink-subtle">Manual return / refund actions are logged.</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-body-sm">
                  <thead className="bg-surface-alt">
                    <tr className="text-left">
                      <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint">Payment</th>
                      <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Run</th>
                      <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
                      <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Bank · Ref</th>
                      <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Amount</th>
                      <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line/60">
                    {payments
                      .slice()
                      .sort((a, b) => {
                        const da = new Date(a.completedAt || a.sentAt || a.scheduledFor || 0).getTime();
                        const db = new Date(b.completedAt || b.sentAt || b.scheduledFor || 0).getTime();
                        return db - da;
                      })
                      .slice(0, 25)
                      .map((p) => {
                      const run = getPaymentRunById(p.runId);
                      const terminal = p.status === "Returned" || p.status === "Refunded" || p.status === "Cancelled";
                      const eligible = p.status === "Sent" || p.status === "Completed" || p.status === "Scheduled";
                      return (
                        <tr key={p.id} className="hover:bg-surface-alt/40">
                          <td className="px-6 py-3 font-mono text-code text-ink-muted">{p.id}</td>
                          <td className="px-3 py-3 font-mono text-code">
                            {run ? <Link to={`/admin/payments/${run.id}`} className="text-brand-700 hover:underline">{run.id}</Link> : <span className="text-ink-hint">—</span>}
                          </td>
                          <td className="px-3 py-3">
                            <StatusBadge entity="payment" status={p.status} />
                            {p.returnReason && <div className="text-[10px] text-red-700 mt-0.5 max-w-[200px] truncate" title={p.returnReason}>{p.returnReason}</div>}
                          </td>
                          <td className="px-3 py-3 font-mono text-code text-ink-subtle">
                            <div>{format.bankLast4(p.bankLast4)}</div>
                            <div className="text-[10px] text-ink-hint">{p.refNumber || "—"}</div>
                          </td>
                          <td className="px-3 py-3 text-ink font-semibold tabular-nums text-right">{format.currency(p.amount)}</td>
                          <td className="px-6 py-3 text-right whitespace-nowrap">
                            {terminal ? (
                              <span className="text-caption text-ink-hint">—</span>
                            ) : eligible ? (
                              <div className="inline-flex items-center gap-3">
                                <Gated action="payments.markReturned">
                                  <button data-testid={`member-mark-returned-${p.id}`}
                                          onClick={() => openAction("returned", p.id)}
                                          className="text-caption font-semibold text-red-700 hover:text-red-800">
                                    Mark returned
                                  </button>
                                </Gated>
                                <Gated action="payments.markRefunded">
                                  <button data-testid={`member-mark-refunded-${p.id}`}
                                          onClick={() => openAction("refunded", p.id)}
                                          className="text-caption font-semibold text-amber-700 hover:text-amber-800">
                                    Refund
                                  </button>
                                </Gated>
                              </div>
                            ) : (
                              <span className="text-caption text-ink-hint">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          <div className="bg-surface border border-line rounded-xl p-6">
            <div className="eyebrow mb-3">Lifetime stats</div>
            <dl className="space-y-3">
              <Stat label="Earned" value={format.currency(lifetimeCompleted)} accent />
              <Stat label="Pending" value={format.currency(pending)} />
              <Stat label="Reservations" value={reservations.length} />
              <Stat label="Cards approved" value={reservations.reduce((a, r) => a + r.cardsApproved, 0)} />
              <Stat label="MFA" value={m.mfaEnabled ? "Enabled" : "Off"} />
            </dl>
          </div>

          <div className="bg-surface border border-line rounded-xl p-6">
            <div className="eyebrow mb-4">Recent activity</div>
            {memberAudit.length === 0 ? (
              <p className="text-caption text-ink-subtle">No activity logged.</p>
            ) : (
              <ol className="space-y-3 text-caption">
                {memberAudit.slice(0, 6).map((e) => (
                  <li key={e.id}>
                    <div className="text-ink-muted">{e.description}</div>
                    <div className="text-ink-hint">{format.relative(e.at)} · <span className="font-mono">{e.action}</span></div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </aside>
      </div>

      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="size-10 rounded-md bg-amber-50 text-amber-700 grid place-items-center mb-3"><ShieldOff className="size-5" /></div>
            <DialogTitle>Suspend {m.firstName} {m.lastName}?</DialogTitle>
            <DialogDescription>
              The member will be unable to make new reservations or submissions. Existing reservations remain active. Reactivating any time restores all access.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Internal reason (audit-logged, member never sees this)…"
                    className="min-h-[100px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendOpen(false)}>Cancel</Button>
            <Button onClick={() => { setSuspendOpen(false); updateMemberStatus(m.id, "Suspended", reason); toast.success(`${m.firstName} suspended`); }}
                    disabled={!reason.trim()}
                    data-testid="confirm-suspend-btn"
                    className="bg-amber-600 hover:bg-amber-700 text-white">Suspend</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual return / refund — member-detail variant */}
      <Dialog open={!!actionDialog} onOpenChange={(v) => !v && setActionDialog(null)}>
        <DialogContent data-testid="member-return-refund-dialog">
          <DialogHeader>
            <DialogTitle>{actionDialog?.kind === "returned" ? "Mark returned" : "Mark refunded"}</DialogTitle>
            <DialogDescription>
              {actionDialog?.kind === "returned"
                ? "Record the ACH return code. This is a manual status change — no funds move."
                : "Record why this payment is being refunded. This is a manual status change — no funds move."}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-caption font-semibold text-ink mb-1.5 block">
              {actionDialog?.kind === "returned" ? "Return code / reason" : "Refund reason"}
            </label>
            <input value={actionReason} onChange={(e) => setActionReason(e.target.value)}
                   data-testid="member-action-reason-input"
                   className="w-full h-9 px-3 border border-line rounded-md text-body-sm bg-surface focus:border-brand-700 focus:ring-2 focus:ring-brand-100 outline-none" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button onClick={confirmAction} data-testid="member-confirm-action-btn"
                    className={actionDialog?.kind === "returned" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-amber-600 hover:bg-amber-700 text-white"}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-caption text-ink-hint mb-0.5">{icon}{label}</div>
      <div className="text-ink">{value || "—"}</div>
    </div>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div className="flex justify-between items-baseline">
      <dt className="text-caption text-ink-subtle">{label}</dt>
      <dd className={`text-body-sm font-semibold tabular-nums ${accent ? "text-brand-700" : "text-ink"}`}>{value}</dd>
    </div>
  );
}
